import React, { useState } from "react";
import { User } from "../types";
import { Logo } from "../components/Logo";
import { supabase } from "../services/supabaseClient";

interface SignupProps {
  onLogin: (user: User) => void;
  onNavigate: (page: string) => void;
}

type DbUserRow = {
  id: string;
  email: string | null;
  name: string | null;
  xp: number | null;
  level: number | null;
  role: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  subscription_end_date: string | null;
  created_at: string | null;
};

function mapDbUserToAppUser(row: DbUserRow): User {
  return {
    id: row.id,
    email: row.email ?? "",
    name: row.name ?? "",
    xp: row.xp ?? 0,
    level: row.level ?? 1,
    role: (row.role ?? "user") as any,
    subscriptionStatus: (row.subscription_status ?? "free") as any,
    subscriptionPlan: (row.subscription_plan ?? undefined) as any,
    subscriptionEndDate: row.subscription_end_date ?? undefined,
    createdAt: row.created_at ?? undefined,
  };
}

export const Signup: React.FC<SignupProps> = ({ onLogin, onNavigate }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchProfile = async (userId: string): Promise<User> => {
    const { data, error } = await supabase.from("users").select("*").eq("id", userId).single();
    if (error) throw error;
    return mapDbUserToAppUser(data as DbUserRow);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting) return;

    setSubmitting(true);
    setErrorMsg("");

    try {
      // 1) Cria usuário no Supabase Auth
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { name }, // salva "name" em user_metadata [page:0]
        },
      });

      if (signUpError) {
        setErrorMsg(signUpError.message || "Erro ao criar conta.");
        return;
      }

      const userId = signUpData.user?.id;
      if (!userId) {
        setErrorMsg("Conta criada, mas não foi possível identificar o usuário.");
        return;
      }

      // 2) Busca perfil (public.users) — você confirmou que está sendo criado também
      const profile = await fetchProfile(userId);
      onLogin(profile);
    } catch (err: any) {
      setErrorMsg(err?.message || "Erro inesperado no cadastro. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col animate-in fade-in duration-700 overflow-x-hidden">
      <header className="w-full flex justify-center pt-8 pb-4">
        <div className="flex flex-col items-center gap-1 group cursor-pointer" onClick={() => onNavigate("landing")}>
          <Logo size="md" className="group-hover:scale-110 transition-transform duration-500" />
          <div className="text-center">
            <h2 className="text-xl font-black text-slate-900 tracking-tight leading-none uppercase">UroInsights</h2>
            <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.4em] mt-1">Academy</p>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full lg:w-[40%] max-w-md bg-white p-12 lg:p-16 rounded-[4rem] shadow-[0_60px_120px_rgba(0,0,0,0.07)] border border-slate-100 relative overflow-hidden">
          <div className="mb-12 text-center lg:text-left relative z-10">
            <h3 className="text-4xl font-black text-slate-900 tracking-tighter mb-3">Bem-vindo(a)</h3>
            <p className="text-slate-400 font-bold text-sm">Crie seu perfil e comece a ganhar XP.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
            {errorMsg && (
              <div className="p-4 bg-red-50 text-red-700 text-sm font-black rounded-2xl border border-red-100">
                {errorMsg}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                Nome Completo
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-3xl transition-all font-bold text-slate-800 outline-none shadow-sm"
                placeholder="Dr. Nome de Exemplo"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                E-mail Profissional
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-3xl transition-all font-bold text-slate-800 outline-none shadow-sm"
                placeholder="doutor@uroinsights.com"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">
                Senha Secreta
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-5 bg-slate-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-3xl transition-all font-bold text-slate-800 outline-none shadow-sm"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-6 mt-6 bg-blue-600 text-white rounded-[2rem] font-black text-xl shadow-2xl shadow-blue-100 hover:bg-blue-700 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? "Criando..." : "Criar Perfil Academy"}
            </button>
          </form>

          <div className="mt-12 text-center relative z-10 pt-8 border-t border-slate-50">
            <p className="text-slate-400 font-bold text-sm">
              Já possui acesso?{" "}
              <button
                type="button"
                onClick={() => onNavigate("login")}
                className="text-blue-600 font-black hover:underline ml-1"
              >
                Fazer login
              </button>
            </p>
          </div>
        </div>
      </div>

      <footer className="py-12 text-center opacity-40">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">&copy; 2025 UroInsights Academy</p>
      </footer>
    </div>
  );
};
